'use client';

import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play } from 'lucide-react';
import { getImageUrl, IMAGE_SIZES, TMDBMovie } from '../lib/tmdb';
import { Button } from './ui/button';
import { useTrailerRotation } from '../hooks/useTrailerRotation';
import { useIsTVDevice } from '../hooks/useTVNavigation';
import { useTVNavigation as useTVNavigationContext } from '../context/TVNavigationContext';

interface AllMoviesData {
  trending: TMDBMovie[];
  popular: TMDBMovie[];
  lists: any[];
}

interface HeroBillboardProps {
  movie: TMDBMovie;
  allMovies: AllMoviesData;
  onPlay: (movie: TMDBMovie) => void;
}

export default function HeroBillboard({ movie, onPlay }: HeroBillboardProps) {
  const [imageError, setImageError] = useState(false);
  const isTV = useIsTVDevice();
  const playButtonRef = useRef<HTMLButtonElement>(null);
  
  // Register nodes with navigation system
  const { registerNode, updateNodeNeighbors } = useTVNavigationContext();
  
  useEffect(() => {
    if (!isTV) return;
    
    // Register nodes with actual refs
    if (playButtonRef.current) {
      registerNode('hero-play-button', playButtonRef);
    }
  }, [isTV, registerNode]);
  
  // Update neighbors - up goes to profile button, down will be set dynamically by parent
  useEffect(() => {
    if (!isTV) return;
    
    updateNodeNeighbors('hero-play-button', {
      up: 'profile-button',  // Go to profile/signin button when pressing up
      down: undefined  // Will be updated dynamically by parent when rows are available
    });
  }, [isTV, updateNodeNeighbors]);
  
  // Use the trailer rotation hook for all trailer logic
  const {
    currentMovie,
    trailerVideoId,
    showTrailer,
    trailerError,
    containerRef
  } = useTrailerRotation();

  // Use currentMovie from hook, fallback to prop movie
  const displayMovie = currentMovie.id ? currentMovie : movie;
  
  const backdropUrl = getImageUrl(displayMovie.backdrop_path, IMAGE_SIZES.backdrop.original);
  const year = displayMovie.release_date ? new Date(displayMovie.release_date).getFullYear() : '';
  const rating = displayMovie.vote_average ? displayMovie.vote_average.toFixed(1) : 'N/A';

  // Fade animation variants
  const fadeVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const fadeTransition = {
    duration: 0.5,
    ease: 'easeInOut' as const
  };

  return (
    <div ref={containerRef} className="relative h-[70vh] lg:h-[85vh] w-full overflow-hidden">
      {/* Background video or image */}
      <motion.div
        initial={{ scale: 1.1 }}
        animate={{ scale: 1 }}
        transition={{ duration: 1.5, ease: 'easeOut' }}
        className="absolute inset-0"
      >
        <AnimatePresence mode="wait">
          {showTrailer && trailerVideoId && !trailerError ? (
            <motion.div
              key={trailerVideoId}
              variants={fadeVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={fadeTransition}
              className="w-full h-full relative overflow-hidden"
              style={{
                width: '100vw',
                height: '100%',
                marginLeft: 'calc(-50vw + 50%)',
                marginRight: 'calc(-50vw + 50%)'
              }}
            >
              <div
                id={`youtube-player-${trailerVideoId}`}
                ref={(el) => {
                  // Trigger player creation when element is mounted
                  if (el && trailerVideoId) {
                    console.log('🎬 Element mounted, notifying hook');
                  }
                }}
                className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                style={{
                  width: '177.77vh',
                  height: '100vh',
                  minWidth: '100vw',
                  minHeight: '56.25vw'
                }}
              />
            </motion.div>
          ) : (
            <motion.img
              key={`image-${displayMovie.id}`}
              variants={fadeVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={fadeTransition}
              src={imageError ? '/placeholder-movie.png' : backdropUrl}
              alt={displayMovie.title}
              className="w-full h-full object-cover"
              fetchPriority="high"
              onError={() => {
                if (!imageError) {
                  console.error('Failed to load backdrop image for:', displayMovie.title);
                  setImageError(true);
                }
              }}
            />
          )}
        </AnimatePresence>
        
        {/* Gradients for better text readability */}
        <div className="absolute inset-0 hero-gradient" />
        <div className="absolute inset-0 hero-gradient-left" />
      </motion.div>

      {/* Content */}
      <div className="relative h-full px-6 sm:px-8 lg:px-16 xl:px-20 flex items-end pb-20 lg:pb-28">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="max-w-2xl space-y-4 lg:space-y-5"
        >
          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="text-4xl sm:text-5xl lg:text-7xl font-bold mb-4 drop-shadow-2xl"
          >
            {displayMovie.title}
          </motion.h1>

          {/* Meta info */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex items-center gap-4 mb-4 text-sm lg:text-base"
          >
            {year && <span className="font-semibold text-green-400">{year}</span>}
            <span className="flex items-center gap-1">
              <svg
                className="w-5 h-5 text-yellow-400"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <span className="font-semibold">{rating}</span>
            </span>
            <span className="px-2 py-1 border border-gray-400 text-xs rounded">HD</span>
          </motion.div>

          {/* Overview */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="text-base lg:text-lg mb-6 line-clamp-3 text-gray-200 drop-shadow-lg max-w-xl"
          >
            {displayMovie.overview}
          </motion.p>

          {/* Action buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="flex flex-wrap gap-3 mt-2"
            role="group"
            aria-label="Movie actions"
          >
            <Button
              ref={playButtonRef}
              onClick={() => onPlay(displayMovie)}
              onKeyDown={(e) => {
                if (isTV && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault()
                  e.stopPropagation()
                  onPlay(displayMovie)
                }
              }}
              size="lg"
              tabIndex={isTV ? 0 : undefined}
              className="bg-white text-black hover:bg-white/90 font-semibold px-6 lg:px-8 py-2.5 lg:py-3 gap-2 transition-transform hover:scale-105 shadow-lg"
              aria-label={`Play ${displayMovie.title}`}
            >
              <Play className="w-5 h-5 lg:w-6 lg:h-6 fill-current" aria-hidden="true" />
              <span className="text-base lg:text-lg">Play</span>
            </Button>
          </motion.div>
        </motion.div>
      </div>

      {/* Bottom fade to blend with content below */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#141414] to-transparent" />
    </div>
  );
}